import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';
import { calculateDiscountedPrice } from '../utils/discountUtils';
import { useLanguage } from './LanguageContext';
import keywordsData from '../lang/keywords.json';

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const [cart, setCart] = useState([]);
  const [cartCurrency, setCartCurrency] = useState('MYR');
  const [deliverFees, setDeliverFees] = useState(0);
  const [serviceChargePercent, setServiceChargePercent] = useState(0);
  const [serviceChargeValue, setServiceChargeValue] = useState(0);
  const [thresholdDiscount, setThresholdDiscount] = useState({ applicable: false, amount: 0, percent: 0, min_amount: 0 , thresholdvalue: 0 });
  const [subtotalBeforeThreshold, setSubtotalBeforeThreshold] = useState(0);
  const [subtotalAfterThreshold, setSubtotalAfterThreshold] = useState(0);
  const [cartOpen, setCartOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [tableScan, setTableScan] = useState(Number(localStorage.getItem('tableId')) || 0);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const clearCart = async () => {
    try {
      if (user) {
        await api.delete('/cart/clear');
      }
      setCart([]);
      setDeliverFees(0);
      setServiceChargeValue(0);
      setThresholdDiscount({ applicable: false, amount: 0, percent: 0, min_amount: 0, thresholdvalue: 0 });
      setSubtotalBeforeThreshold(0);
      setSubtotalAfterThreshold(0);
      localStorage.removeItem('tableId');
      localStorage.removeItem('current_rest_id');
      setTableScan(0);
      setSnackbar({
        open: true,
        message: language === 'ar' ? keywordsData.delete_cart_txt_ar : keywordsData.delete_cart_txt_en,
        severity: 'success'
      });
    } catch (err) {
      console.error('Error clearing cart:', err);
    }
  };

  const fetchCart = async () => {
    if (!tableScan) {
      if (!user) return;
    }
    try {
      setLoading(true);
      const restaurantId = localStorage.getItem('current_rest_id') ;
      if(!restaurantId) return;
      
      const response = await api.get('/cart', {
        params: { tableScan: tableScan,restID:restaurantId }
      });
      
      const data = response.data;
      setCart(data?.cart_items || data?.items || data);
      setDeliverFees(data?.delivery_value || data?.summary?.delivery_fee || 0);
      setCartCurrency(data?.currency);
      // Safely set service charge values
      if (data?.service_charge) {
        setServiceChargeValue(data.service_charge.amount || 0);
        setServiceChargePercent(data.service_charge.percent || 0);
      }
      
      // Set threshold discount data
      if (data?.threshold_discount) {
        setThresholdDiscount({
          applicable: data.threshold_discount.applicable || false,
          amount: data.threshold_discount.amount || 0,
          percent: data.threshold_discount.percent || 0,
          min_amount: data.threshold_discount.min_amount || 0,
          thresholdvalue: data.threshold_discount.thresholdvalue || 0
        });
      }
      
      setSubtotalBeforeThreshold(data?.subtotal_before_threshold || 0);
      setSubtotalAfterThreshold(data?.subtotal_after_threshold || 0);
      
    } catch (err) {
      console.error('Error fetching cart:', err);
    } finally {
      setLoading(false);
    }
  };

// In CartContext.js - Alternative approach (accepts object)
// In CartContext.js - Updated to handle both signatures
const addToCart = async (productOrObject, quantity = 1, selectedSize = null, selectedExtras = []) => {
  
  let productId;
  let finalQuantity;
  let finalSelectedSize;
  let finalSelectedExtras;
  let promotionData = null;
  
  // Check if first parameter is an object (has product_id property)
  if (typeof productOrObject === 'object' && productOrObject !== null && productOrObject.product_id) {
    // It's the customized item object from ProductOptionsDialog
    console.log('Adding customized item with object:', productOrObject);
    
    productId = productOrObject.product_id;
    finalQuantity = productOrObject.quantity || 1;
    finalSelectedSize = productOrObject.selected_size || null;
    finalSelectedExtras = productOrObject.selected_extras || [];
    
    // Extract promotion data if it exists
    if (productOrObject.applied_promotion_id) {
      promotionData = {
        applied_promotion_id: productOrObject.applied_promotion_id,
        applied_promotion_data: productOrObject.applied_promotion_data,
        free_reward_item_id: productOrObject.free_reward_item_id,
        free_reward_item_type: productOrObject.free_reward_item_type,
        promotion_qualifying_amount: productOrObject.promotion_qualifying_amount
      };
    }
  } else {
    // It's a simple product ID (from the simple add to cart button)
    console.log('Adding simple product with ID:', productOrObject);
    
    productId = productOrObject;
    finalQuantity = quantity;
    finalSelectedSize = selectedSize;
    finalSelectedExtras = selectedExtras;
  }

  if (!tableScan) {
    if (!user) {
      window.location.href = '../login';
      return;
    }
  }

  try {
    // Calculate final price
    let finalPrice = 0;
    let basePrice = 0;
    
    const productResponse = await api.get(`/products/${productId}`);
    const product = productResponse.data;
    
    basePrice = product.final_price || product.price || 0;
    finalPrice = basePrice;

    // Add size price
    if (finalSelectedSize) {
      finalPrice += finalSelectedSize.price || 0;
    }

    // Add extras prices
    if (finalSelectedExtras.length > 0) {
      finalPrice += finalSelectedExtras.reduce((sum, extra) => sum + (extra.price || 0), 0);
    }

    // Prepare cart data
    const cartData = {
      product_id: productId,
      quantity: finalQuantity,
      language,
      tableScan,
      selected_size: finalSelectedSize,
      selected_extras: finalSelectedExtras,
      base_price: basePrice,
      final_unit_price: finalPrice
    };

    // Add promotion data if available
    if (promotionData) {
      if (promotionData.applied_promotion_id) {
        cartData.applied_promotion_id = promotionData.applied_promotion_id;
      }
      if (promotionData.applied_promotion_data) {
        cartData.applied_promotion_data = promotionData.applied_promotion_data;
      }
      if (promotionData.free_reward_item_id) {
        cartData.free_reward_item_id = promotionData.free_reward_item_id;
      }
      if (promotionData.free_reward_item_type) {
        cartData.free_reward_item_type = promotionData.free_reward_item_type;
      }
      if (promotionData.promotion_qualifying_amount) {
        cartData.promotion_qualifying_amount = promotionData.promotion_qualifying_amount;
      }
    }

    console.log('Sending cart data:', cartData);

    await api.post('/cart', cartData);
    await fetchCart();
    
    setSnackbar({
      open: true,
      message: language === 'ar' ? keywordsData.add_cart_txt_ar : keywordsData.add_cart_txt_en,
      severity: 'success'
    });
  } catch (err) {
    console.error('Error adding to cart:', err);
    setSnackbar({
      open: true,
      message: language === 'ar' ? keywordsData.add_fall_cart_txt_ar : keywordsData.add_fall_cart_txt_en,
      severity: 'error'
    });
  }
};

const addToCart1 = async (productId, quantity = 1) => {
    if (!tableScan) {
      if (!user) {
        window.location.href = '../login';
        return;
      }
    }

    try {
      await api.post('/cart', { product_id: productId, quantity, language, tableScan });
      await fetchCart();
      setSnackbar({
        open: true,
        message: language === 'ar' ? keywordsData.add_cart_txt_ar : keywordsData.add_cart_txt_en,
        severity: 'success'
      });
    } catch (err) {
      console.error('Error adding to cart:', err);
      setSnackbar({
        open: true,
        message: language === 'ar' ? keywordsData.add_fall_cart_txt_ar : keywordsData.add_fall_cart_txt_en,
        severity: 'error'
      });
    }
  };

  const updateQuantity = async (cartItemId, quantity) => {
    try {
      await api.put(`/cart/${cartItemId}`, { quantity });
      await fetchCart();
    } catch (err) {
      console.error('Error updating quantity:', err);
    }
  };

  const removeFromCart = async (cartItemId) => {
    try {
      await api.delete(`/cart/delete/${cartItemId}`);
      await fetchCart();
      setSnackbar({
        open: true,
        message: language === 'ar' ? keywordsData.delete_product_cart_txt_ar : keywordsData.delete_product_cart_txt_en,
        severity: 'success'
      });
    } catch (err) {
      console.error('Error removing from cart:', err);
    }
  };

  // Calculate product total from cart items
  const productTotal = cart.reduce(
    (total, item) => total + (item.final_price || item.product?.price || 0) * item.quantity,
    0
  );

  // Calculate total savings from product discounts
  const productSavings = cart.reduce(
    (savings, item) => savings + (item.discount_applied || 0) * item.quantity,
    0
  );

  // FIXED: Use subtotalAfterThreshold from backend if available, otherwise calculate
  const effectiveSubtotal = subtotalAfterThreshold > 0 ? subtotalAfterThreshold : productTotal;

  // Calculate cart total with all fees
  const cartTotal = effectiveSubtotal + deliverFees + serviceChargeValue;

  // Calculate total savings including threshold discount
  const totalSavings = productSavings + (thresholdDiscount.amount || 0);

  const cartCount = cart.reduce((count, item) => count + item.quantity, 0);

  // Get individual item price (with discount if applicable)
  const getItemPrice = (item) => {
    return item.final_price || item.product?.price || 0;
  };

  // Get original price before discount
  const getOriginalPrice = (item) => {
    return item.product?.price || 0;
  };

  // Check if item has discount
  const hasDiscount = (item) => {
    return item.discount_applied > 0 || (item.product?.discount_percent > 0);
  };

  useEffect(() => {
    if (!tableScan) {
      if (!user) {
        setCart([]);
      } else {
        fetchCart();
      }
    } else {
      fetchCart();
    }
  }, [user, tableScan]);

  return (
    <CartContext.Provider
      value={{
        cart,
        cartOpen,
        setCartOpen,
        addToCart,
        updateQuantity,
        removeFromCart,
        cartTotal,
        productTotal,
        totalSavings,
        productSavings,
        deliverFees,
        serviceChargePercent,
        serviceChargeValue,
        thresholdDiscount,
        subtotalBeforeThreshold,
        subtotalAfterThreshold,
        effectiveSubtotal,
        cartCount,
        loading,
        clearCart,
        getItemPrice,
        getOriginalPrice,
        hasDiscount,
        handleCloseSnackbar,
        snackbar,
        setTableScan,
        fetchCart,
        cartCurrency
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);